import * as React from "react"
import { Command as CmdkCommand } from "cmdk"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@lib/cn"
import { Popover } from "./Popover"
import { Command } from "./Command"

export interface ComboboxOption {
  value: string
  label: string
  keywords?: string[]
  disabled?: boolean
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  emptyMessage?: string
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
  triggerClassName?: string
}

export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = "Select…",
      emptyMessage = "No results.",
      searchPlaceholder = "Search…",
      disabled,
      className,
      triggerClassName,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false)
    const selected = options.find((o) => o.value === value)

    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          ref={ref}
          disabled={disabled}
          className={cn(
            "ui-control",
            "inline-flex h-8 w-full items-center justify-between gap-2 rounded-md px-3 text-small",
            "bg-white text-cloverhound-gray-900 dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50",
            "border border-cloverhound-gray-300 dark:border-cloverhound-gray-800",
            "transition-colors duration-quick ease-out-quad",
            "hover:border-cloverhound-gray-400 dark:hover:border-cloverhound-gray-700",
            "focus:outline-none focus:border-cloverhound-indigo-400 focus:ring-2 focus:ring-cloverhound-indigo-500/30",
            "disabled:cursor-not-allowed disabled:opacity-70",
            "data-[popup-open]:border-cloverhound-indigo-400",
            triggerClassName,
          )}
        >
          <span className={cn("truncate", !selected && "text-cloverhound-gray-600")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-cloverhound-gray-600" aria-hidden="true" />
        </Popover.Trigger>
        <Popover.Content className={cn("w-[var(--anchor-width)] min-w-[14rem] p-0", className)} sideOffset={6}>
          <CmdkCommand
            className="flex flex-col overflow-hidden"
          >
            <Command.Input placeholder={searchPlaceholder} />
            <Command.List>
              <Command.Empty>{emptyMessage}</Command.Empty>
              <Command.Group>
                {options.map((option) => (
                  <Command.Item
                    key={option.value}
                    value={`${option.label} ${option.keywords?.join(" ") ?? ""}`}
                    disabled={option.disabled}
                    onSelect={() => {
                      onChange?.(option.value)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        "size-4",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    {option.label}
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </CmdkCommand>
        </Popover.Content>
      </Popover.Root>
    )
  },
)
Combobox.displayName = "Combobox"
